import React, { useMemo } from "react";
import "../../day-plan-styles/ProductionBreakdown.css";
import { useTranslation } from "@preppa/translation";
import SSRIcon from "@ingka/ssr-icon";
import Button from "@ingka/button";
import dataIcon from "@ingka/ssr-icon/paths/data-chart";
import { PlanData } from "../../../../types/types";
import { useDataContext } from "../../../../data-context/DataContextProvider";
import {
  DAILY_DAYVIEW_ID,
  HOURLY_DAYVIEW_ID,
} from "../../../../utils/timeSegmentation";
import {
  getDisplayConversion,
  formatVolume,
} from "../../../../utils/planDataHelpers";

interface ProductionBreakdownProps {
  planData: PlanData;
  onClose: () => void;
}

function ProductionBreakdown({ planData, onClose }: ProductionBreakdownProps) {
  const { dayViews } = useDataContext();
  const { translate } = useTranslation();

  const displayConversion = getDisplayConversion(planData);
  const conversionFactor = displayConversion?.conversionFactor ?? 1;

  const hourlyVolumes = useMemo(() => {
    const dtos = planData.itemSalesPlanDtos;
    const totalPlanned = dtos.reduce(
      (sum, dto) => sum + (dto.plannedVol || 0),
      0,
    );
    const usePlanned = totalPlanned > 0;

    const volumes = new Map<number, number>();
    dtos.forEach((dto) => {
      const hour = parseInt(dto.plannedDayHour.substring(8, 10), 10);
      if (!isNaN(hour)) {
        const vol = usePlanned ? dto.plannedVol || 0 : dto.forecastVol || 0;
        volumes.set(hour, (volumes.get(hour) || 0) + vol);
      }
    });
    return volumes;
  }, [planData]);

  const fullDayTotal = useMemo(() => {
    let total = 0;
    hourlyVolumes.forEach((vol) => {
      total += vol;
    });
    return total;
  }, [hourlyVolumes]);

  const hourlyBreakdown = useMemo(() => {
    let firstNonZero = -1;
    let lastNonZero = -1;
    for (let h = 0; h <= 23; h++) {
      if ((hourlyVolumes.get(h) || 0) > 0) {
        if (firstNonZero === -1) firstNonZero = h;
        lastNonZero = h;
      }
    }

    if (firstNonZero === -1) return [];

    const hours: { label: string; volume: number }[] = [];
    for (let h = firstNonZero; h <= lastNonZero; h++) {
      const vol = hourlyVolumes.get(h) || 0;
      hours.push({
        label: `${h.toString().padStart(2, "0")}:00 - ${h.toString().padStart(2, "0")}:59`,
        volume: vol,
      });
    }
    return hours;
  }, [hourlyVolumes]);

  const customBreakdowns = useMemo(() => {
    return dayViews
      .filter(
        (dv) =>
          dv.splitTimes &&
          dv.splitTimes.length > 0 &&
          dv.dayViewId !== DAILY_DAYVIEW_ID &&
          dv.dayViewId !== HOURLY_DAYVIEW_ID &&
          dv.dayViewIdParent !== DAILY_DAYVIEW_ID &&
          dv.dayViewIdParent !== HOURLY_DAYVIEW_ID,
      )
      .map((dv) => {
        const boundaries = dv
          .splitTimes!.map((st) => parseInt(st.splitTime.split(":")[0], 10))
          .filter((h) => !isNaN(h))
          .sort((a, b) => a - b);

        const combined = [8, ...boundaries].filter(
          (h, i, arr) => arr.indexOf(h) === i,
        );
        combined.sort((a, b) => a - b);
        const allBounds = [...combined, 21];

        const slots: { label: string; volume: number }[] = [];

        for (let i = 0; i < allBounds.length - 1; i++) {
          const startHour = allBounds[i];
          const endHour = allBounds[i + 1] - 1;

          let slotVol = 0;
          for (let h = startHour; h <= endHour; h++) {
            slotVol += hourlyVolumes.get(h) || 0;
          }

          slots.push({
            label: `${startHour.toString().padStart(2, "0")}:00 - ${endHour.toString().padStart(2, "0")}:59`,
            volume: slotVol,
          });
        }

        return {
          name: dv.timeslotNameLocal || dv.timeslotName || "Custom",
          slots,
        };
      });
  }, [dayViews, hourlyVolumes]);

  return (
    <div className="production-breakdown-wrapper">
      <div className="production-breakdown-header">
        <SSRIcon paths={dataIcon} color="black" viewBox="2 2 24 24" />
        <span>{translate("dayPlan.breakdown.estimationBreakdown")}</span>
      </div>
      <div className="production-breakdown-item-name">{planData.localName}</div>
      <div className="production-breakdown-content">
        <div className="breakdown-section">
          <div className="breakdown-section-header">{translate("dayPlan.breakdown.fullDay")}</div>
          <div className="breakdown-row full-day-row">
            <span className="breakdown-label">{translate("dayPlan.breakdown.total")}</span>
            <span className="breakdown-value">
              {formatVolume(fullDayTotal / conversionFactor)}
            </span>
          </div>
        </div>
        <div className="breakdown-section">
          <div className="breakdown-section-header">{translate("dayPlan.breakdown.hourly")}</div>
          {hourlyBreakdown.map((slot) => (
            <div
              key={slot.label}
              className={`breakdown-row ${slot.volume === 0 ? "breakdown-row-zero" : ""}`}
            >
              <span className="breakdown-label">{slot.label}</span>
              <span className="breakdown-value">
                {formatVolume(slot.volume / conversionFactor)}
              </span>
            </div>
          ))}
        </div>
        {customBreakdowns.map((custom) => (
          <div key={custom.name} className="breakdown-section">
            <div className="breakdown-section-header">{custom.name}</div>
            {custom.slots.map((slot) => (
              <div
                key={slot.label}
                className={`breakdown-row ${slot.volume === 0 ? "breakdown-row-zero" : ""}`}
              >
                <span className="breakdown-label">{slot.label}</span>
                <span className="breakdown-value">
                  {formatVolume(slot.volume / conversionFactor)}
                </span>
              </div>
            ))}
          </div>
        ))}
      </div>
      <div className="production-breakdown-footer">
        <Button text={translate("dayPlan.breakdown.close")} type="secondary" size="xsmall" onClick={onClose} />
      </div>
    </div>
  );
}

export default ProductionBreakdown;
